import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Box,
  Checkbox,
  FormControlLabel,
  Collapse,
  Divider,
  Typography,
  Switch,
  IconButton,
  Button,
} from '@mui/material';
import { ChevronRight, ExpandMore } from '@mui/icons-material';

interface DeploymentRiskConfig {
  id: string;
  name: string;
  enabled: boolean;
  enabledSubFilters: Set<string>;
  expanded: boolean;
}

interface PlatformRiskConfigurationModalProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (
    selectedDeployments: string[],
    categoryDataTypes: Record<string, string[]>
  ) => void;
  selectedDeploymentTypes: string[];
  categoryDataTypes: Record<string, string[]>;
  availableCategories: string[];
  availableSubFilters: Record<string, Set<string>>;
  insightTypeMap: Record<string, string>;
}

export const PlatformRiskConfigurationModal: React.FC<PlatformRiskConfigurationModalProps> = ({
  open,
  onClose,
  onApplyFilters,
  selectedDeploymentTypes,
  categoryDataTypes,
  availableCategories,
  availableSubFilters,
  insightTypeMap,
}) => {
  const [deploymentConfigs, setDeploymentConfigs] = useState<DeploymentRiskConfig[]>([]);

  // Simplified initialization - create configs from props
  const initialConfigs = useMemo(() => {
    return availableCategories.map((category) => {
      const categoryId = category.toLowerCase();
      const categorySubFilters = availableSubFilters[category] || new Set();
      const categoryDataType = categoryDataTypes[categoryId] || [];

      // Initialize with enabled sub-filters from props
      const enabledSubFilters = new Set<string>();
      categoryDataType.forEach((subFilter) => {
        if (categorySubFilters.has(subFilter)) {
          enabledSubFilters.add(subFilter);
        }
      });

      return {
        id: categoryId,
        name: category,
        enabled: selectedDeploymentTypes.includes(categoryId),
        enabledSubFilters,
        expanded: false,
      };
    });
  }, [availableCategories, availableSubFilters, selectedDeploymentTypes, categoryDataTypes]);

  // Initialize configs when modal opens
  useEffect(() => {
    if (open) {
      setDeploymentConfigs(initialConfigs);
    }
  }, [open, initialConfigs]);

  // Simplified handlers with cleaner logic
  const updateConfig = useCallback((id: string, updates: Partial<DeploymentRiskConfig>) => {
    setDeploymentConfigs((prev) =>
      prev.map((config) => (config.id === id ? { ...config, ...updates } : config))
    );
  }, []);

  const handleToggleDeployment = useCallback(
    (deploymentId: string) => {
      updateConfig(deploymentId, {
        enabled: !deploymentConfigs.find((c) => c.id === deploymentId)?.enabled,
      });
    },
    [updateConfig, deploymentConfigs]
  );

  const handleExpandToggle = useCallback(
    (deploymentId: string) => {
      updateConfig(deploymentId, {
        expanded: !deploymentConfigs.find((c) => c.id === deploymentId)?.expanded,
      });
    },
    [updateConfig, deploymentConfigs]
  );

  const handleSubFilterToggle = useCallback(
    (deploymentId: string, subFilterType: string) => {
      const config = deploymentConfigs.find((c) => c.id === deploymentId);
      if (!config) return;

      const newEnabledSubFilters = new Set(config.enabledSubFilters);
      if (newEnabledSubFilters.has(subFilterType)) {
        newEnabledSubFilters.delete(subFilterType);
      } else {
        newEnabledSubFilters.add(subFilterType);
      }

      updateConfig(deploymentId, { enabledSubFilters: newEnabledSubFilters });
    },
    [deploymentConfigs, updateConfig]
  );

  const handleApply = useCallback(() => {
    const selectedDeployments = deploymentConfigs
      .filter((config) => config.enabled)
      .map((config) => config.id);

    const newCategoryDataTypes: Record<string, string[]> = {};
    deploymentConfigs.forEach((config) => {
      if (config.enabled) {
        newCategoryDataTypes[config.id] = Array.from(config.enabledSubFilters);
      }
    });

    onApplyFilters(selectedDeployments, newCategoryDataTypes);
    onClose();
  }, [deploymentConfigs, onApplyFilters, onClose]);

  const hasEnabledDeployments = deploymentConfigs.some((c) => c.enabled);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '24px',
        },
      }}
    >
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 4 }}>
        Score configuration
      </Typography>

      <DialogContent sx={{ padding: 0, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Enable Score for deployments
        </Typography>

        <Stack spacing={0}>
          {deploymentConfigs.map((config, index) => (
            <Box key={config.id}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {config.enabled ? (
                    <IconButton
                      size="small"
                      onClick={() => handleExpandToggle(config.id)}
                      sx={{ p: 0.5 }}
                    >
                      {config.expanded ? (
                        <ExpandMore sx={{ fontSize: '2rem' }} />
                      ) : (
                        <ChevronRight sx={{ fontSize: '2rem' }} />
                      )}
                    </IconButton>
                  ) : (
                    <Box sx={{ width: '28px' }} />
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {config.name}
                  </Typography>
                </Box>
                <Switch
                  checked={config.enabled}
                  onChange={() => handleToggleDeployment(config.id)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#10b981',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#10b981',
                    },
                  }}
                />
              </Box>

              <Collapse in={config.enabled && config.expanded}>
                <Box sx={{ pl: 7, pb: 2 }}>
                  <Stack spacing={1.5}>
                    {Array.from(availableSubFilters[config.name] || new Set()).map(
                      (subFilterType) => (
                        <FormControlLabel
                          key={subFilterType}
                          control={
                            <Checkbox
                              checked={config.enabledSubFilters.has(subFilterType)}
                              onChange={() => handleSubFilterToggle(config.id, subFilterType)}
                              sx={{
                                color: '#cbd5e1',
                                padding: '6px',
                                '&.Mui-checked': {
                                  color: '#1e40af',
                                },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: '#475569' }}>
                              {insightTypeMap[subFilterType] || subFilterType}
                            </Typography>
                          }
                          sx={{ margin: 0 }}
                        />
                      )
                    )}
                  </Stack>
                </Box>
              </Collapse>

              {index < deploymentConfigs.length - 1 && <Divider sx={{ borderColor: '#e2e8f0' }} />}
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ padding: 0, gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <Button
          onClick={onClose}
          variant="text"
          sx={{
            color: '#64748b',
            fontSize: '1.4rem',
            fontWeight: 500,
            textTransform: 'none',
            px: 2,
            py: 1,
            '&:hover': {
              backgroundColor: '#f1f5f9',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={!hasEnabledDeployments}
          sx={{
            backgroundColor: '#1e3a8a',
            fontSize: '1.4rem',
            fontWeight: 500,
            textTransform: 'none',
            px: 3,
            py: 1,
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: '#1e40af',
            },
            '&:disabled': {
              backgroundColor: '#cbd5e1',
              color: '#94a3b8',
            },
          }}
        >
          Save & apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
